import { useEffect, useState } from "react";
import useCrmApi from "../../../components/hook/crm-req";

// GET /api/v1/dashboard/warehouse-metrics — see
// data-warehouse-dashboard-ui-integration.md. Same CRM/warehouse backend
// (api.crm.dalmlabs.com/api/v1) as the rest of this app's warehouse-prefixed
// endpoints (profile-explorer/traits' /traits/warehouse/*), so this goes
// through useCrmApi with its base already including /api/v1 — the endpoint
// here is just "/dashboard/warehouse-metrics". tenant_id is derived
// server-side from the auth token; never send one from the browser. 200 with
// an empty `data` array means the tenant genuinely has no metrics yet, not
// an error. `is_sample: true` rows are sample/demo data standing in for a
// real pipeline, not live production totals — every card sourced from one
// must stay visibly badged as such.
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("authToken")}` });

export interface WarehouseMetric {
  metric_group: string;
  metric_key: string;
  metric_label: string;
  metric_value: number;
  metric_unit: "count" | "percent" | string;
  sort_order: number;
  is_sample: boolean;
  as_of: string;
}

export interface WarehouseMetricsResponse {
  data: WarehouseMetric[];
  total: number;
  as_of: string;
  source: string;
}

export function useWarehouseMetrics() {
  const { loading, makeRequest } = useCrmApi("/dashboard/warehouse-metrics", "GET", authHeaders());
  const [data, setData] = useState<WarehouseMetricsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status === 200) setData(json);
      else if (status === 400) setError("This workspace isn't linked to a data warehouse with a valid id yet.");
      else if (status === 401) setError("Please sign in again.");
      else if (status === 503) setError("The data warehouse integration isn't configured yet.");
      else setError("Couldn't load dashboard metrics.");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = data?.data || [];
  const byGroup = (group: string) => rows.filter((m) => m.metric_group === group).sort((a, b) => a.sort_order - b.sort_order);

  // Tries the exact metric_key(s) first (snake_case of the card's own
  // label, matching the one confirmed example — "Total Visitors" ->
  // "total_visitors"), then falls back to matching metric_label
  // case-insensitively against any of the given labels — per the doc's own
  // "use metric_label for display" guidance, labels are a second stable
  // handle when a key guess misses. Returns undefined (never a fabricated
  // 0) when nothing in the group matches any candidate.
  const findMetric = (group: string, keys: string[], labels: string[] = []): WarehouseMetric | undefined => {
    const groupRows = byGroup(group);
    for (const key of keys) {
      const hit = groupRows.find((m) => m.metric_key === key);
      if (hit) return hit;
    }
    const lowerLabels = labels.map((l) => l.toLowerCase());
    return groupRows.find((m) => lowerLabels.includes(m.metric_label.toLowerCase()));
  };

  const formatValue = (m?: WarehouseMetric): string => {
    if (!m) return "—";
    if (m.metric_unit === "percent") return `${m.metric_value}%`;
    if (m.metric_unit === "count") return Math.round(m.metric_value).toLocaleString();
    return String(m.metric_value);
  };

  return { data, loading, error, byGroup, findMetric, formatValue, asOf: data?.as_of };
}
